export interface PreviewFile {
  name: string
  url: string
  type?: string
  size?: number
}

export type PreviewKind =
  | 'image'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'docx'
  | 'spreadsheet'
  | 'text'
  | 'unsupported'

export function safeFileUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined
  try {
    const url = new URL(value, window.location.href)
    return ['http:', 'https:'].includes(url.protocol) ? url.href : undefined
  } catch {
    return undefined
  }
}

// Старые поля хранят группы urls или массив url, новые — response.files и отдельные файлы.
export function normalizePreviewFiles(value: any): PreviewFile[] {
  if (!value) return []
  if (Array.isArray(value)) return value.flatMap(normalizePreviewFiles)
  if (value.response?.files) return normalizePreviewFiles(value.response.files)
  if (value.urls?.length) return normalizePreviewFiles(value.urls)
  if (Array.isArray(value.url)) {
    return value.url.flatMap(url =>
      normalizePreviewFiles(typeof url === 'string' ? { ...value, url } : url),
    )
  }
  if (value.status === 'uploading' || value.status === 'error') return []
  const url = safeFileUrl(typeof value === 'string' ? value : value.url)
  if (!url) return []
  let name = typeof value === 'object' && (value['name-field'] || value.name)
  if (!name) {
    try {
      name = decodeURIComponent(new URL(url).pathname.split('/').pop() || '')
    } catch {
      /* Некорректное кодирование имени не мешает открыть файл. */
    }
  }
  return [{ url, name: name || 'Файл', type: value.type, size: value.size }]
}

export function previewKind(file: PreviewFile): PreviewKind {
  const mime = (file.type || '').split(';')[0].toLowerCase()
  const path = new URL(file.url).pathname
  const extension = (
    file.name.match(/\.([a-z0-9]+)$/i)?.[1] ||
    path.match(/\.([a-z0-9]+)$/i)?.[1] ||
    ''
  ).toLowerCase()
  if (/^(png|jpe?g|gif|webp|bmp|svg|avif|ico)$/.test(extension) || mime.startsWith('image/'))
    return 'image'
  if (/^(mp4|mov|m4v|webm|avi|mkv|3gp|ogv)$/.test(extension) || mime.startsWith('video/'))
    return 'video'
  if (/^(mp3|wav|ogg|m4a|aac|flac|opus)$/.test(extension) || mime.startsWith('audio/'))
    return 'audio'
  if (extension === 'pdf' || mime === 'application/pdf') return 'pdf'
  if (
    extension === 'docx' ||
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  )
    return 'docx'
  if (
    /^(xlsx|xls|csv|ods)$/.test(extension) ||
    [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'application/vnd.oasis.opendocument.spreadsheet',
    ].includes(mime)
  )
    return 'spreadsheet'
  if (/^(txt|log|md|json|xml|html?|css|ya?ml)$/.test(extension) || mime.startsWith('text/'))
    return 'text'
  return 'unsupported'
}

export function decodePreviewText(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(buffer)
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(buffer)
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer)
  } catch {
    return new TextDecoder('windows-1251').decode(buffer)
  }
}

export const DOCUMENT_PREVIEW_LIMIT = 25 * 1024 * 1024

export type PreviewDocumentLoader = (file: PreviewFile, signal: AbortSignal) => Promise<ArrayBuffer>

export async function loadPreviewDocument(
  file: PreviewFile,
  signal: AbortSignal,
): Promise<ArrayBuffer> {
  const tooLarge = () =>
    new Error(
      'Для предпросмотра документов доступны файлы до 25 МБ. Скачайте файл, чтобы открыть его на устройстве.',
    )
  if (Number(file.size) > DOCUMENT_PREVIEW_LIMIT) throw tooLarge()
  const response = await fetch(file.url, { signal, credentials: 'same-origin' })
  if (!response.ok)
    throw new Error('Не удалось получить файл. Попробуйте открыть его в новой вкладке или скачать.')
  if (Number(response.headers.get('content-length')) > DOCUMENT_PREVIEW_LIMIT) {
    await response.body?.cancel()
    throw tooLarge()
  }
  if (!response.body) {
    const buffer = await response.arrayBuffer()
    if (buffer.byteLength > DOCUMENT_PREVIEW_LIMIT) throw tooLarge()
    return buffer
  }
  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  let size = 0
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      size += value.byteLength
      if (size > DOCUMENT_PREVIEW_LIMIT) {
        await reader.cancel()
        throw tooLarge()
      }
      chunks.push(value)
    }
  } finally {
    reader.releaseLock()
  }
  const buffer = new Uint8Array(size)
  let offset = 0
  chunks.forEach(chunk => {
    buffer.set(chunk, offset)
    offset += chunk.byteLength
  })
  return buffer.buffer
}
